import { Title } from "@angular/platform-browser";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { optionsAvgTRes, optionsErrorCount, optionsThroughput, optionsHitsPSec } from "@app/_constants";

import { optionsUsers } from "@app/_constants/perflab-results-option-users";

import { environment } from "@environments/environment";
import { ToastrService } from "ngx-toastr";
import { PerformanceService } from "../performance.service";
import { interval, Subscription } from "rxjs";
import { Role } from "@app/_models/role";
import { ChartConfigService } from "@app/_services";
import * as moment from 'moment-timezone';

@Component({
  selector: "app-performance-results",
  templateUrl: "./performance-results.component.html",
  styleUrls: ["./performance-results.component.less"],
  providers: [ChartConfigService],
})
export class PerformanceResultsComponent implements OnInit, OnDestroy {
  readonly ADMIN = Role[0].role;
  readonly SUPER_ADMIN = Role[4].role;

  readonly INTERVAL_TIME = 15;

  executedTaskId: string;
  testCompleted = false;
  perResults: any;
  intervalTimer: Subscription;
  queueRes = [];
  abortTriggered: boolean;

  // Graph veriables
  dataUsers = [];
  datasetOverride: any;
  labelsAvgTRes = [];
  dataHitsPSec: Array<any> = [{}];
  dataVUsers: Array<any> = [{}];
  dataThroughput: Array<any> = [{}];
  dataAvgTRes: Array<any> = [{}];
  dataErrorCount: Array<any> = [{}];
  series = [];
  labels: string[] = [];
  lineChartType = "line";
  optionsUsers: any = optionsUsers;
  optionsHitsPSec: any = optionsHitsPSec;
  optionsThroughput: any = optionsThroughput;
  optionsAvgTRes: any = optionsAvgTRes;
  optionsErrorCount: any = optionsErrorCount;
  colors: any = ["green"];
  length: number;

  machineStats = [];
  isAutoRefresh = true;
  autoRefreshInterval = this.INTERVAL_TIME;
  isAuthorized = false;
  authorizationMsg = "";
  created_date: any;
  currentPSTTime: any;
  diff: any;
  direction: string = 'asc';
  sortDirection = true;
  loadTestDataDetails = true;
  chartsSummary = false;
  responseSummary = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private perfService: PerformanceService,
    private router: Router,
    private toastr: ToastrService,
    private chart: ChartConfigService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(() => {
      this.executedTaskId = this.route.snapshot.params.id;
    });
    this.perResults = {
      testcaseExecSumm: null,
    };
    if (this.route.snapshot.routeConfig.path === "result/:id/queue") {
      this.getTestcaseTopDetails(this.executedTaskId);
      this.queueRes = [];
    } else if (this.route.snapshot.routeConfig.path === "result/:id") {
      this.resultTestCase();
    }
  }

  setTooltips(data: any[]) {
    this.optionsAvgTRes.plugins.tooltip = this.chart.configureChartToolTip(data);
    this.optionsErrorCount.plugins.tooltip = this.chart.configureChartToolTip(data);
    this.optionsHitsPSec.plugins.tooltip = this.chart.configureChartToolTip(data);
    this.optionsThroughput.plugins.tooltip = this.chart.configureChartToolTip(data);
    this.optionsUsers.plugins.tooltip = this.chart.configureChartToolTip(data, true);
  }

  resultTestCase() {
    if (this.executedTaskId === "") {
      this.toastr.info("Please execute a test or select from queue");
      this.router.navigateByUrl("/performance/loadTesting");
      return false;
    }
    this.getTestcaseTopDetails(this.executedTaskId);
  }

  getTestcaseTopDetails(executedTaskId?: string) {
    const id = executedTaskId || this.executedTaskId;
    const timerInterval = interval(this.autoRefreshInterval * 1000);
    this.perfService.getTestCaseTopDetails(id).subscribe((data) => {
      if (data.code === 200) {
        const respData = data.response;
        this.isAuthorized = true;
        this.testCompleted = respData.executionCompleted;
        this.perResults.testcaseExecSumm = respData;

        this.perResults.testcaseExecSumm.hitsPerSec = respData.noOfHits / respData.elapsedTime || 0;

        if (this.perResults.testcaseExecSumm.name) {
          this.titleService.setTitle("Perf-Result | " + this.perResults.testcaseExecSumm.name);
        }
        this.drawCharts(id);

        if (!respData.executionCompleted && this.isAutoRefresh) {
          if (this.intervalTimer) {
            this.intervalTimer.unsubscribe();
          }
          this.intervalTimer = timerInterval.subscribe(() => {
            this.getTestcaseTopDetails(id);
          });
        } else {
          if (this.intervalTimer) {
            this.intervalTimer.unsubscribe();
          }
        }
        this.fetchMachinesFroExecution(respData.performanceRequest.executionId);
        const todayDate = moment();
        this.currentPSTTime = todayDate.tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss');
        this.created_date = this.perResults.testcaseExecSumm.performanceRequest.startTime;
        this.diff = this.getDataDiff(new Date(this.created_date), new Date(this.currentPSTTime))
      } else if (data.code == 204) {
        this.isAuthorized = false;
        this.authorizationMsg = data.message;
      }
    });
  }
  getDataDiff(startDate, endDate) {
    const diff = endDate.getTime() - startDate.getTime();
    const days = Math.floor(diff / (60 * 60 * 24 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000)) - (days * 24);
    const minutes = Math.floor(diff / (60 * 1000)) - ((days * 24 * 60) + (hours * 60));
    const seconds = Math.floor(diff / 1000) - ((days * 24 * 60 * 60) + (hours * 60 * 60) + (minutes * 60));
    return { day: days, hour: hours, minute: minutes, second: seconds };
  }

  drawCharts(executedTaskId: any) {
    this.perfService.graphDetails(executedTaskId).subscribe((data) => {
      if (data.code === 200) {
        this.queueRes = data.response.map(obj => {
          obj.passedHits = obj.noOfHits - obj.errorCount;
          return obj;
        })
        if(this.perResults.testcaseExecSumm.loadTestType === 'WEB'){
          let sortedArray = (this.queueRes || []).sort((a,b)=>{
              return (a['sequence'] > b['sequence']) ? 1 : -1;
          })
          this.queueRes = sortedArray;
        }
        this.colors = this.chart.chartColors();
        this.setGraph(data.response);
        this.setTooltips(this.queueRes);
      }
    });
  }

  setGraph(responseData: any[]) {
    const dataResp = [...responseData];

    let foundGraphLabel = false;

    for (const index in dataResp) {
      let sampleRes = dataResp[index].relaySampleResults;
      if (sampleRes == null) continue;

      /* Create the label for duration x-axis */

      sampleRes = sampleRes.sort((a,b) => a.duration > b.duration);

      if(this.labelsAvgTRes.length === 0) {
        this.labelsAvgTRes = sampleRes.map((s: { duration: number }) => s.duration);
      } else {
        let tempResult = sampleRes.map((s: { duration: number }) => s.duration);
        let concatArray = this.labelsAvgTRes.concat(tempResult);
        concatArray.sort();
        concatArray.sort(function(a, b){return parseInt(a) - parseInt(b)});
        this.labelsAvgTRes = concatArray;
      }

      const chartMethod = this.chart.chartColors()[0];

      this.dataHitsPSec[index] = {
        data: sampleRes.map((r) => {
          let tempObject = {
            y: r.sampleNumber,
            x: r.duration
          }
          return tempObject;
        }),
        ...chartMethod,
      };

      this.dataVUsers[index] = {
        data: sampleRes.map((r) => {
          let tempObject = {
            y: r.runningUsers,
            x: r.duration
          }
          return tempObject;
        }),
        ...chartMethod,
      };
      this.dataThroughput[index] = {
        data: sampleRes.map((r) => {
          let tempObject = {
            y: r.bytesPerSecond,
            x: r.duration
          }
          return tempObject;
        }),
        ...chartMethod,
      };

      this.dataAvgTRes[index] = {
        data: sampleRes.map((r) => {
          let tempObject = {
            y: r.sla,
            x: r.duration
          }
          return tempObject;
        }),
        ...chartMethod,
      };

      this.dataErrorCount[index] = {
        data: sampleRes.map((r) => {
          let tempObject = {
            y: r.errorCount,
            x: r.duration
          }
          return tempObject;
        }),
        ...chartMethod,
      };

      this.length = this.dataVUsers[index].data.filter(item => item === 0).length
      this.dataHitsPSec = [...this.dataHitsPSec];
      this.dataVUsers = [...this.dataVUsers];
      this.dataAvgTRes = [...this.dataAvgTRes];
      this.dataThroughput = [...this.dataThroughput];
      this.dataErrorCount = [...this.dataErrorCount];
    }
    this.labelsAvgTRes = this.labelsAvgTRes.filter((item,index) => this.labelsAvgTRes.indexOf(item) === index);

    const noOfusers = this.perResults.testcaseExecSumm.performanceRequest.noOfUsers;
    const durationtCount = this.labelsAvgTRes.length;

    this.dataUsers = Array.from({ length: durationtCount }, (v, i: number) => {
      if (i === 0) return 0;
      return noOfusers;
    });
  }

  setAutoRefresh() {
    const stopwatch = interval(this.autoRefreshInterval * 1000);
    if (this.intervalTimer) {
      this.intervalTimer.unsubscribe();
    }
    if (this.isAutoRefresh) {
      this.intervalTimer = stopwatch.subscribe(() => {
        this.getTestcaseTopDetails();
      });
    }
  }

  stopAutoRefresh() {
    this.intervalTimer.unsubscribe();
  }

  fileDownload(gridFsId: string) {
    window.open(`${environment.apiUrl}file/download/${gridFsId}`);
  }

  formalRunFileDownload(id: string, perfId: string) {
    window.open(`${environment.apiUrl}result/performance/download/s3/${id}?request=${perfId}`);
  }

  abortRunningTest() {
    this.perfService.abortResults(this.executedTaskId).subscribe((data) => {
      if (data.code === 200) {
        this.abortTriggered = true;
        this.toastr.success(data.message);
        this.router.navigate(["."], { relativeTo: this.route });
      } else {
        this.abortTriggered = false;
        this.testCompleted = true;
        this.toastr.error(data.message);
      }
    });
  }

  fetchMachinesFroExecution(executionId) {
    this.perfService.getMachineStats(executionId).subscribe((data) => {
      this.machineStats = data.response;
    });
  }

  sortByColumn(columnName: string) {

    this.direction = (this.sortDirection) ? 'desc' : 'asc';
    // let direction = 'desc';
    let sortedArray;
    if (columnName === 'name') {
      sortedArray = (this.queueRes || []).sort((a, b) => {
        let val1 = a[columnName].toLowerCase();
        let val2 = b[columnName].toLowerCase();
        if (val1 > val2) {
          return (this.direction === 'asc') ? 1 : -1;
        }
        if (val1 < val2) {
          return (this.direction === 'asc') ? -1 : 1;
        }
        return 0;
      })

    } else {
      sortedArray = (this.queueRes || []).sort((a, b) => {
        if (a[columnName] > b[columnName]) {
          return (this.direction === 'asc') ? 1 : -1;
        }
        if (a[columnName] < b[columnName]) {
          return (this.direction === 'asc') ? -1 : 1;
        }
        return 0;
      })
    }
    this.sortDirection = !this.sortDirection;
    // return sortedArray;
    this.queueRes = sortedArray;
  }

  autoRefreshMsg() {
    if (!this.isAutoRefresh) {
      return "Auto refresh is paused";
    }

    const hitCount = this.perResults.testcaseExecSumm?.noOfHits;
    if (hitCount !== 0) {
      return 'Please wait test is running ..';
    } else if (this.diff.minute >= 2 && hitCount === 0) {
      return 'Applying the Provided Setting';
    } else if (this.diff.minute <= 1 && hitCount === 0) {
      return 'Uploading your Testcases';
    }
  }

  secondsToDhms(seconds) {
    seconds = parseInt(seconds, 10);
    var days = Math.floor(seconds / (3600*24));
    seconds  -= days*3600*24;
    var hrs   = Math.floor(seconds / 3600);
    seconds  -= hrs*3600;
    var mnts = Math.floor(seconds / 60);
    seconds  -= mnts*60;
    var dDisplay = days > 0 ? days + (days == 1 ? "D  " : "D  ") : "";
    var hDisplay = hrs > 0 ? (hrs < 10 ? "0" : "") + hrs : "00";
    var mDisplay = mnts > 0 ? (mnts < 10 ? ":0" : ":") + mnts : ":00";
    var sDisplay = seconds > 0 ? (seconds < 10 ? ":0" : ":") + seconds : ":00";
    return dDisplay + hDisplay + mDisplay + sDisplay;
  }

  ngOnDestroy(): void {
    if (this.intervalTimer) this.intervalTimer.unsubscribe();
  }
}
